import React, { useCallback, useEffect, useMemo, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import { useNavigate } from "react-router-dom";
import BottomTab from "../../../../components/pagination/BottomTab";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../redux/loader/loaderActions";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import AllPaymentReport from "./AllPaymentReport";
import DrugHistoryModal from "./DrugHistoryModal";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import FormDate from "../../../../components/formFeild/FormDate";
import FormCheck from "../../../../components/formFeild/FormCheck";
import { toast } from "react-toastify";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";

interface FormData {
  payments: boolean;
  startDate: string;
  endDate: string;
}

function toAmount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatAmount(value: unknown): string {
  return toAmount(value).toLocaleString();
}

type DentistOption = {
  _id: string;
  title?: string;
  lastName?: string;
};

function dentistLabel(d: DentistOption): string {
  return [d.title, d.lastName].filter(Boolean).join(" ").trim() || "Dentist";
}

function getPaidTotal(item: {
  cash?: unknown;
  card?: unknown;
  bankTransfer?: unknown;
}) {
  return (
    toAmount(item?.cash) + toAmount(item?.card) + toAmount(item?.bankTransfer)
  );
}

/** Prefer API aggregates; fall back to summing paymentInfo rows. */
function resolveInvoiceAmounts(item: any) {
  const hasAggregates =
    item?.cash != null ||
    item?.card != null ||
    item?.bankTransfer != null ||
    item?.discount != null;

  if (hasAggregates) {
    const cash = toAmount(item?.cash);
    const card = toAmount(item?.card);
    const bankTransfer = toAmount(item?.bankTransfer);
    const discount = toAmount(item?.discount);
    const paid = cash + card + bankTransfer;
    const due =
      item?.due != null
        ? toAmount(item.due)
        : toAmount(item?.totalPrice) - paid - discount;
    return { cash, card, bankTransfer, discount, paid, due };
  }

  const totals = (item?.paymentInfo ?? []).reduce(
    (
      acc: {
        card: number;
        cash: number;
        bankTransfer: number;
        discount: number;
      },
      curr: any
    ) => ({
      card: acc.card + toAmount(curr?.card),
      cash: acc.cash + toAmount(curr?.cash),
      bankTransfer: acc.bankTransfer + toAmount(curr?.bankTransfer),
      discount: acc.discount + toAmount(curr?.discount),
    }),
    { card: 0, cash: 0, bankTransfer: 0, discount: 0 }
  );
  const paid = totals.card + totals.cash + totals.bankTransfer;
  const due = toAmount(item?.totalPrice) - paid - totals.discount;
  return { ...totals, paid, due };
}

function dentistRowLabel(item: any): string {
  return (
    [item?.dentistName, item?.dentistLastName].filter(Boolean).join(" ").trim() ||
    "—"
  );
}

function dueAdvLabel(dueAmount: number): { label: string; isDue: boolean } {
  if (dueAmount > 0) return { label: "Due ", isDue: true };
  if (dueAmount < 0) return { label: "Adv ", isDue: false };
  return { label: "", isDue: false };
}

const actionBtnClass =
  "inline-flex h-7 w-7 items-center justify-center rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-slate-800";

function PrescriptionButton({
  onClick,
  className = "",
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      title="Prescription / drug history"
      aria-label="Prescription / drug history"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`${actionBtnClass} shrink-0 bg-teal-50 text-teal-600 hover:bg-teal-100 focus:ring-teal-500 dark:bg-teal-900/40 dark:text-teal-300 dark:hover:bg-teal-800/50 ${className}`}
    >
      <MedicationOutlinedIcon sx={{ fontSize: 15 }} />
    </button>
  );
}

function InvoiceRowActions({
  treatmentId,
  onViewTreatment,
  onViewInvoice,
}: {
  treatmentId?: string;
  onViewTreatment: (id: string) => void;
  onViewInvoice: (id: string) => void;
}) {
  if (!treatmentId) return null;

  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        title="View treatment"
        aria-label="View treatment"
        onClick={(e) => {
          e.stopPropagation();
          onViewTreatment(treatmentId);
        }}
        className={`${actionBtnClass} bg-violet-50 text-violet-600 hover:bg-violet-100 focus:ring-violet-500 dark:bg-violet-900/40 dark:text-violet-300 dark:hover:bg-violet-800/50`}
      >
        <MedicalServicesOutlinedIcon sx={{ fontSize: 15 }} />
      </button>
      <button
        type="button"
        title="View invoice"
        aria-label="View invoice"
        onClick={(e) => {
          e.stopPropagation();
          onViewInvoice(treatmentId);
        }}
        className={`${actionBtnClass} bg-indigo-50 text-indigo-600 hover:bg-indigo-100 focus:ring-indigo-500 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-800/50`}
      >
        <ReceiptLongOutlinedIcon sx={{ fontSize: 15 }} />
      </button>
    </div>
  );
}

function AllLastPayments() {
  const navigate = useNavigate();
  const [report, setreport] = useState(false);
  const [openpayments, setopenpayments] = useState(false);
  const [payment, setpayment] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterDoctorId, setFilterDoctorId] = useState<string>("all");
  const [dentists, setDentists] = useState<DentistOption[]>([]);
  const [filterSession, setFilterSession] = useState<string>("all");
  const [sessions, setSessions] = useState<
    { sessionName: string; startTime?: string; endTime?: string }[]
  >([]);
  const [drugModal, setDrugModal] = useState<{
    treatmentId: string;
    patientName?: string;
    invoiceNo?: string;
  } | null>(null);
  const [dateRangeMode, setDateRangeMode] = useState<"today" | "all">("all");
  const search = useSelector((state: RootState) => state.searching.patientData);
  const methods = useForm<FormData>();
  const { watch, reset, setValue } = methods;
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const dispatch = useDispatch();

  useEffect(() => {
    baseUrl
      .get("/appointment/findAllAppointmentDentist")
      .then((response) => {
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setDentists(
          list
            .map((d: DentistOption) => ({
              _id: String(d?._id ?? ""),
              title: d?.title,
              lastName: d?.lastName,
            }))
            .filter((d: DentistOption) => d._id)
        );
      })
      .catch((error) => {
        console.error(error);
        setDentists([]);
      });
  }, []);

  useEffect(() => {
    baseUrl
      .get("/client/setting/clinicSession/list", {
        params: { onlyActive: "1" },
      })
      .then((response) => {
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setSessions(
          list
            .map((s: any) => ({
              sessionName: String(s?.sessionName ?? "").trim(),
              startTime: s?.startTime,
              endTime: s?.endTime,
            }))
            .filter((s: { sessionName: string }) => s.sessionName)
        );
      })
      .catch((error) => {
        console.error(error);
        setSessions([]);
      });
  }, []);

  const getData = useCallback(
    (
      keyword: string,
      pageNum: number,
      startDate?: string,
      endDate?: string,
      doctorId?: string,
      sessionName?: string,
      options?: { disablePagination?: boolean }
    ) => {
      dispatch(setloadeActive());
      baseUrl
        .get("payment/getAllPayment", {
          headers: {
            keyword,
            page: options?.disablePagination ? 1 : pageNum,
            ...(options?.disablePagination ? { limit: "all" } : {}),
          },
          params: {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            doctorId:
              doctorId && doctorId !== "all" ? doctorId : undefined,
            sessionName:
              sessionName && sessionName !== "all" ? sessionName : undefined,
          },
        })
        .then((response) => {
          dispatch(setloadeDeactive());
          setpayment(response.data.data);
          setCount(response.data.count);
        })
        .catch((error) => {
          dispatch(setloadeDeactive());
          console.error(error);
        });
    },
    [dispatch]
  );

  const fetchPayments = useCallback(
    (
      keyword: string,
      pageNum: number,
      doctorId: string,
      from?: string,
      to?: string,
      sessionName?: string,
      disablePagination?: boolean
    ) => {
      getData(keyword, pageNum, from, to, doctorId, sessionName, {
        disablePagination,
      });
    },
    [getData]
  );

  useEffect(() => {
    const activeSearchTerm = searchTerm || search || "";
    const from = startDate && endDate ? startDate : undefined;
    const to = startDate && endDate ? endDate : undefined;
    const isToday = dateRangeMode === "today";
    fetchPayments(
      activeSearchTerm,
      isToday ? 1 : page,
      filterDoctorId,
      from,
      to,
      filterSession,
      isToday
    );
  }, [
    search,
    searchTerm,
    page,
    filterDoctorId,
    filterSession,
    dateRangeMode,
    startDate,
    endDate,
    fetchPayments,
  ]);

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      if (value.payments) {
        setopenpayments(true);
      } else {
        setopenpayments(false);
      }
    });
    return () => unsubscribe();
  }, [watch]);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const closeModal = useCallback(() => {
    setreport(false);
  }, []);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      if (!data.startDate || !data.endDate) {
        toast.info("Select both From and To dates to download the report.");
        return;
      }
      const keyword = (searchTerm || search || "").trim();
      try {
        const response = await baseUrl.get("client/payment/paymentReport", {
          responseType: "blob",
          params: {
            startDate: data.startDate,
            endDate: data.endDate,
            payments: data.payments,
            ...(filterDoctorId !== "all" ? { doctorId: filterDoctorId } : {}),
            ...(filterSession !== "all" ? { sessionName: filterSession } : {}),
            ...(keyword ? { keyword } : {}),
          },
        });
        if (response.status === 204) {
          toast.info("No data found for the selected filters.");
          return;
        }
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const url = URL.createObjectURL(pdfBlob);
        const fileName = `payment-report-${data.startDate}-to-${data.endDate}.pdf`;

        const preview = window.open(url, "_blank");
        if (!preview) {
          toast.warn("Allow pop-ups to preview the report, or use Download.");
        }

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        toast.success("Payment report generated.");
      } catch (error: unknown) {
        const err = error as { response?: { status?: number } };
        if (err?.response?.status === 204) {
          toast.info("No data found for the selected filters.");
          return;
        }
        toast.error("Could not generate the payment report.");
        console.error("Error downloading the PDF", error);
      }
    },
    [filterDoctorId, filterSession, search, searchTerm]
  );

  const methodReset = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      reset();
      setSearchTerm("");
      setFilterDoctorId("all");
      setFilterSession("all");
      setDateRangeMode("all");
      setPage(1);
    },
    [reset]
  );

  const applyDateRangeMode = useCallback(
    (mode: "today" | "all") => {
      setDateRangeMode(mode);
      setPage(1);
      if (mode === "today") {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const todayStr = `${yyyy}-${mm}-${dd}`;
        setValue("startDate", todayStr, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("endDate", todayStr, {
          shouldDirty: true,
          shouldValidate: true,
        });
        return;
      }
      setValue("startDate", "", { shouldDirty: true });
      setValue("endDate", "", { shouldDirty: true });
    },
    [setValue]
  );

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  const handleDoctorFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterDoctorId(e.target.value);
      setPage(1);
    },
    []
  );

  const handleSessionFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterSession(e.target.value);
      setPage(1);
    },
    []
  );

  const openTreatment = useCallback(
    (treatmentId: string) => {
      navigate(
        `/client/treatment-diagnose/add-treatment/treatment-table/${treatmentId}`
      );
    },
    [navigate]
  );

  const openInvoice = useCallback(
    (treatmentId: string) => {
      navigate(`/client/payments/invoice/${treatmentId}`);
    },
    [navigate]
  );

  const openDrugHistory = useCallback((item: {
    _id?: string;
    fullName?: string;
    invoiceNo?: string;
  }) => {
    if (!item?._id) return;
    setDrugModal({
      treatmentId: item._id,
      patientName: item.fullName,
      invoiceNo: item.invoiceNo,
    });
  }, []);

  const closeDrugModal = useCallback(() => {
    setDrugModal(null);
  }, []);

  const fieldLabelClass =
    "text-slate-700 dark:text-slate-300 text-[13px] font-medium leading-[1.4]";
  const cellClass = "px-2.5 py-1.5 text-xs";

  const todaySummary = useMemo(() => {
    if (dateRangeMode !== "today" || payment.length === 0) return null;

    return payment.reduce(
      (acc, item: any) => {
        const amounts = resolveInvoiceAmounts(item);
        return {
          totalPrice: acc.totalPrice + toAmount(item?.totalPrice),
          discount: acc.discount + amounts.discount,
          cash: acc.cash + amounts.cash,
          card: acc.card + amounts.card,
          bankTransfer: acc.bankTransfer + amounts.bankTransfer,
          paid: acc.paid + amounts.paid,
          due: acc.due + amounts.due,
          count: acc.count + 1,
        };
      },
      {
        totalPrice: 0,
        discount: 0,
        cash: 0,
        card: 0,
        bankTransfer: 0,
        paid: 0,
        due: 0,
        count: 0,
      }
    );
  }, [dateRangeMode, payment]);

  const summaryDueLabel = todaySummary
    ? dueAdvLabel(todaySummary.due)
    : null;

  const showDentistColumn = filterDoctorId === "all";
  const tableColCount = showDentistColumn ? 11 : 10;
  const summaryLabelColSpan = showDentistColumn ? 3 : 2;

  return (
    <div className="space-y-2.5">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-2.5 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="flex flex-1 flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="flex w-full flex-col gap-1 sm:max-w-md">
              <label className={fieldLabelClass}>Search</label>
              <input
                type="search"
                placeholder="Invoice, patient, dentist..."
                value={searchTerm}
                onChange={handleSearch}
                className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all placeholder:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex w-36 shrink-0 flex-col gap-1">
                <label className={fieldLabelClass}>Doctor</label>
                <select
                  value={filterDoctorId}
                  onChange={handleDoctorFilter}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="all">All</option>
                  {dentists.map((d) => (
                    <option key={d._id} value={d._id}>
                      {dentistLabel(d)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex w-40 shrink-0 flex-col gap-1">
                <label className={fieldLabelClass}>Session</label>
                <select
                  value={filterSession}
                  onChange={handleSessionFilter}
                  className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="all">All Sessions</option>
                  {sessions.map((s) => (
                    <option key={s.sessionName} value={s.sessionName}>
                      {s.sessionName}
                      {s.startTime && s.endTime
                        ? ` (${s.startTime}-${s.endTime})`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
              {dateRangeMode !== "today" ? (
                <>
                  <FormDate label="From" name="startDate" sameSize />
                  <FormDate label="To" name="endDate" sameSize />
                </>
              ) : null}
              <div className="flex flex-col gap-1">
                <span className={fieldLabelClass}>Period</span>
                <div className="flex h-8 w-fit items-center gap-1 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-700">
                  {(
                    [
                      { value: "today", label: "Today" },
                      { value: "all", label: "All" },
                    ] as const
                  ).map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => applyDateRangeMode(value)}
                      className={`h-7 rounded-md px-2.5 text-xs font-medium transition-all ${
                        dateRangeMode === value
                          ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-600 dark:text-indigo-300"
                          : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex h-8 items-center">
                <FormCheck label="Payment Breakdown" name="payments" />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="submit"
              className="flex h-8 items-center whitespace-nowrap rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3 text-xs font-medium text-white transition-all hover:shadow-md hover:shadow-primary/20"
            >
              Download PDF
            </button>
            <button
              type="button"
              onClick={methodReset}
              className="flex h-8 items-center whitespace-nowrap rounded-lg border border-slate-200 bg-slate-100 px-3 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              Reset
            </button>
          </div>
        </form>
      </FormProvider>

      <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
            {count}
          </span>{" "}
          {count === 1 ? "record" : "records"}
          {dateRangeMode === "today" ? (
            <span className="text-slate-400 dark:text-slate-500"> · Today</span>
          ) : startDate && endDate ? (
            <span className="text-slate-400 dark:text-slate-500">
              {" "}
              · {startDate} to {endDate}
            </span>
          ) : (
            <span className="text-slate-400 dark:text-slate-500"> · All</span>
          )}
          {filterDoctorId !== "all" ? (
            <span className="text-slate-400 dark:text-slate-500"> · Doctor filtered</span>
          ) : null}
          {filterSession !== "all" ? (
            <span className="text-slate-400 dark:text-slate-500">
              {" "}
              · {filterSession}
            </span>
          ) : null}
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-600 md:block">
        <table className={`w-full table-fixed ${showDentistColumn ? "min-w-[1000px]" : "min-w-[900px]"}`}>
          <colgroup>
            <col style={{ width: "108px" }} />
            <col />
            {showDentistColumn ? <col /> : null}
            <col style={{ width: "72px" }} />
            <col style={{ width: "68px" }} />
            <col style={{ width: "68px" }} />
            <col style={{ width: "68px" }} />
            <col style={{ width: "68px" }} />
            <col style={{ width: "72px" }} />
            <col style={{ width: "80px" }} />
            <col style={{ width: "108px" }} />
          </colgroup>
          <thead className="border-b border-slate-200 bg-slate-50/90 dark:border-slate-600 dark:bg-slate-700/60">
            <tr>
              <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                Date / Invoice
              </th>
              <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                Patient
              </th>
              {showDentistColumn ? (
                <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                  Dentist
                </th>
              ) : null}
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Total
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Discount
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Cash
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Card
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Bank
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Paid
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Due / Adv
              </th>
              <th className={`${cellClass} text-right font-semibold text-slate-500 dark:text-slate-400`}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-700/80 dark:bg-slate-800">
            {payment.length === 0 ? (
              <tr>
                <td
                  colSpan={tableColCount}
                  className="px-2.5 py-6 text-center text-xs text-slate-500 dark:text-slate-400"
                >
                  No payments found.
                </td>
              </tr>
            ) : (
              payment.map((item: any, index) => {
                const amounts = resolveInvoiceAmounts(item);
                const { label: dueLabel, isDue } = dueAdvLabel(amounts.due);

                return (
                  <React.Fragment key={item?._id ?? index}>
                    <tr className="group transition-colors even:bg-slate-50/40 hover:bg-indigo-50/40 dark:even:bg-slate-800/60 dark:hover:bg-indigo-950/20">
                      <td className={`${cellClass} whitespace-nowrap`}>
                        <div className="font-semibold text-indigo-700 dark:text-indigo-300">
                          #{item?.invoiceNo ?? "—"}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">
                          {item?.updatedAt?.split("T")[0] ?? "—"}
                        </div>
                      </td>
                      <td className={cellClass}>
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span
                            className="min-w-0 truncate font-semibold text-slate-900 dark:text-slate-100"
                            title={item?.fullName}
                          >
                            {item?.fullName || "—"}
                          </span>
                          {item?._id && item?.hasDrugs ? (
                            <PrescriptionButton
                              onClick={() => openDrugHistory(item)}
                            />
                          ) : null}
                        </div>
                      </td>
                      {showDentistColumn ? (
                        <td
                          className={`${cellClass} truncate text-slate-600 dark:text-slate-300`}
                          title={dentistRowLabel(item)}
                        >
                          {dentistRowLabel(item)}
                        </td>
                      ) : null}
                      <td className={`${cellClass} whitespace-nowrap text-right font-medium tabular-nums text-slate-700 dark:text-slate-200`}>
                        {formatAmount(item?.totalPrice)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-orange-600 dark:text-orange-400`}>
                        {formatAmount(amounts.discount)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-emerald-600 dark:text-emerald-400`}>
                        {formatAmount(amounts.cash)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-indigo-600 dark:text-indigo-400`}>
                        {formatAmount(amounts.card)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-sky-600 dark:text-sky-400`}>
                        {formatAmount(amounts.bankTransfer)}
                      </td>
                      <td className={`${cellClass} whitespace-nowrap text-right font-semibold tabular-nums text-emerald-700 dark:text-emerald-300`}>
                        {formatAmount(amounts.paid)}
                      </td>
                      <td
                        className={[
                          cellClass,
                          "whitespace-nowrap text-right font-semibold tabular-nums",
                          isDue
                            ? "text-red-600 dark:text-red-400"
                            : amounts.due < 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-slate-600 dark:text-slate-300",
                        ].join(" ")}
                      >
                        {dueLabel}
                        {formatAmount(Math.abs(amounts.due))}
                      </td>
                      <td className={cellClass}>
                        <InvoiceRowActions
                          treatmentId={item?._id}
                          onViewTreatment={openTreatment}
                          onViewInvoice={openInvoice}
                        />
                      </td>
                    </tr>
                    {openpayments &&
                      item?.paymentInfo?.map((sub: any, subIndex: number) => {
                        const subPaid = getPaidTotal(sub);
                        const subDue = toAmount(sub?.due);
                        const { label: subDueLabel, isDue: subIsDue } =
                          dueAdvLabel(subDue);
                        const subTotal =
                          toAmount(sub?.payable) ||
                          toAmount(sub?.netotal) + toAmount(sub?.discount);
                        return (
                          <tr
                            key={`${item?._id}-${subIndex}`}
                            className="bg-slate-50/60 dark:bg-slate-700/40"
                          >
                            <td className={`${cellClass} pl-4`}>
                              <div className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
                                #{sub?.invoiceNo ?? "—"}
                              </div>
                              <div className="text-[10px] text-slate-500 dark:text-slate-400">
                                {sub?.updatedAt?.split("T")[0] ?? "—"}
                              </div>
                            </td>
                            <td className={cellClass} />
                            {showDentistColumn ? <td className={cellClass} /> : null}
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-slate-600 dark:text-slate-300`}>
                              {formatAmount(subTotal)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-orange-600 dark:text-orange-400`}>
                              {formatAmount(sub?.discount)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-emerald-600 dark:text-emerald-400`}>
                              {formatAmount(sub?.cash)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-indigo-600 dark:text-indigo-400`}>
                              {formatAmount(sub?.card)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-sky-600 dark:text-sky-400`}>
                              {formatAmount(sub?.bankTransfer)}
                            </td>
                            <td className={`${cellClass} whitespace-nowrap text-right tabular-nums text-emerald-700 dark:text-emerald-300`}>
                              {formatAmount(subPaid)}
                            </td>
                            <td
                              className={[
                                cellClass,
                                "whitespace-nowrap text-right font-semibold tabular-nums",
                                subIsDue
                                  ? "text-red-600 dark:text-red-400"
                                  : subDue < 0
                                    ? "text-emerald-600 dark:text-emerald-400"
                                    : "text-slate-600 dark:text-slate-300",
                              ].join(" ")}
                            >
                              {subDueLabel}
                              {formatAmount(Math.abs(subDue))}
                            </td>
                            <td className={cellClass} />
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {todaySummary && (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-100/90 dark:border-slate-500 dark:bg-slate-700/70">
                <td
                  className={`${cellClass} font-bold text-slate-800 dark:text-slate-100`}
                  colSpan={summaryLabelColSpan}
                >
                  Today summary
                  <span className="ml-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    ({todaySummary.count} invoice
                    {todaySummary.count === 1 ? "" : "s"})
                  </span>
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-slate-800 dark:text-slate-100`}>
                  {formatAmount(todaySummary.totalPrice)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-orange-600 dark:text-orange-400`}>
                  {formatAmount(todaySummary.discount)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-emerald-600 dark:text-emerald-400`}>
                  {formatAmount(todaySummary.cash)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-indigo-600 dark:text-indigo-400`}>
                  {formatAmount(todaySummary.card)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-sky-600 dark:text-sky-400`}>
                  {formatAmount(todaySummary.bankTransfer)}
                </td>
                <td className={`${cellClass} whitespace-nowrap text-right font-bold tabular-nums text-emerald-700 dark:text-emerald-300`}>
                  {formatAmount(todaySummary.paid)}
                </td>
                <td
                  className={[
                    cellClass,
                    "whitespace-nowrap text-right font-bold tabular-nums",
                    summaryDueLabel?.isDue
                      ? "text-red-600 dark:text-red-400"
                      : todaySummary.due < 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-slate-700 dark:text-slate-200",
                  ].join(" ")}
                >
                  {summaryDueLabel?.label}
                  {formatAmount(Math.abs(todaySummary.due))}
                </td>
                <td className={cellClass} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="space-y-2 md:hidden">
        {payment.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 px-3 py-5 text-center text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400">
            No payments found.
          </div>
        ) : (
          payment.map((item: any, index) => {
            const amounts = resolveInvoiceAmounts(item);
            const { label: dueLabel, isDue } = dueAdvLabel(amounts.due);

            return (
              <div
                key={item?._id ?? index}
                className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 dark:border-slate-600 dark:bg-slate-800/80"
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <p className="min-w-0 truncate text-xs font-semibold text-slate-900 dark:text-slate-100">
                        {item?.fullName || "—"}
                      </p>
                      {item?._id && item?.hasDrugs ? (
                        <PrescriptionButton
                          onClick={() => openDrugHistory(item)}
                        />
                      ) : null}
                    </div>
                    {showDentistColumn ? (
                      <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                        {dentistRowLabel(item)}
                      </p>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                      #{item?.invoiceNo ?? "—"}
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      {item?.updatedAt?.split("T")[0] ?? "—"}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-[11px] sm:grid-cols-4">
                  <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">Total</p>
                    <p className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">
                      {formatAmount(item?.totalPrice)}
                    </p>
                  </div>
                  <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                    <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Paid</p>
                    <p className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                      {formatAmount(amounts.paid)}
                    </p>
                  </div>
                  <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                    <p className="text-[10px] text-orange-600 dark:text-orange-400">Discount</p>
                    <p className="font-semibold tabular-nums text-orange-700 dark:text-orange-300">
                      {formatAmount(amounts.discount)}
                    </p>
                  </div>
                  <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-700">
                    <p
                      className={`text-[10px] ${
                        isDue
                          ? "text-red-600 dark:text-red-400"
                          : amounts.due < 0
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-slate-500 dark:text-slate-400"
                      }`}
                    >
                      {dueLabel.trim() || "Balance"}
                    </p>
                    <p
                      className={`font-semibold tabular-nums ${
                        isDue
                          ? "text-red-700 dark:text-red-300"
                          : amounts.due < 0
                            ? "text-emerald-700 dark:text-emerald-300"
                            : "text-slate-700 dark:text-slate-300"
                      }`}
                    >
                      {formatAmount(Math.abs(amounts.due))}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex justify-end border-t border-slate-200/80 pt-2 dark:border-slate-600/80">
                  <InvoiceRowActions
                    treatmentId={item?._id}
                    onViewTreatment={openTreatment}
                    onViewInvoice={openInvoice}
                  />
                </div>
              </div>
            );
          })
        )}
        {todaySummary && (
          <div className="rounded-lg border border-slate-300 bg-slate-100 p-2.5 dark:border-slate-500 dark:bg-slate-700/70">
            <p className="mb-2 text-xs font-bold text-slate-800 dark:text-slate-100">
              Today summary
              <span className="ml-1.5 font-medium text-slate-500 dark:text-slate-400">
                ({todaySummary.count} invoice
                {todaySummary.count === 1 ? "" : "s"})
              </span>
            </p>
            <div className="grid grid-cols-2 gap-1.5 text-[11px] sm:grid-cols-4">
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-slate-500 dark:text-slate-400">Total</p>
                <p className="font-bold tabular-nums text-slate-800 dark:text-slate-100">
                  {formatAmount(todaySummary.totalPrice)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Paid</p>
                <p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                  {formatAmount(todaySummary.paid)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-orange-600 dark:text-orange-400">Discount</p>
                <p className="font-bold tabular-nums text-orange-700 dark:text-orange-300">
                  {formatAmount(todaySummary.discount)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p
                  className={`text-[10px] ${
                    summaryDueLabel?.isDue
                      ? "text-red-600 dark:text-red-400"
                      : todaySummary.due < 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-slate-500 dark:text-slate-400"
                  }`}
                >
                  {summaryDueLabel?.label.trim() || "Balance"}
                </p>
                <p
                  className={`font-bold tabular-nums ${
                    summaryDueLabel?.isDue
                      ? "text-red-700 dark:text-red-300"
                      : todaySummary.due < 0
                        ? "text-emerald-700 dark:text-emerald-300"
                        : "text-slate-700 dark:text-slate-200"
                  }`}
                >
                  {formatAmount(Math.abs(todaySummary.due))}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Cash</p>
                <p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                  {formatAmount(todaySummary.cash)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800">
                <p className="text-[10px] text-indigo-600 dark:text-indigo-400">Card</p>
                <p className="font-bold tabular-nums text-indigo-700 dark:text-indigo-300">
                  {formatAmount(todaySummary.card)}
                </p>
              </div>
              <div className="rounded bg-white px-2 py-1.5 dark:bg-slate-800 sm:col-span-2">
                <p className="text-[10px] text-sky-600 dark:text-sky-400">Bank</p>
                <p className="font-bold tabular-nums text-sky-700 dark:text-sky-300">
                  {formatAmount(todaySummary.bankTransfer)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {dateRangeMode !== "today" ? (
        <BottomTab title="Invoices" count={count} onPageChange={onPageChange} />
      ) : null}
      {report && <AllPaymentReport closeModal={closeModal} />}
      {drugModal && (
        <DrugHistoryModal
          treatmentId={drugModal.treatmentId}
          patientName={drugModal.patientName}
          invoiceNo={drugModal.invoiceNo}
          onClose={closeDrugModal}
        />
      )}
    </div>
  );
}

export default AllLastPayments;
